<!--
  AndyMascot.vue · Vue 2 (Options API)

  Mascota de Andy: círculo de color plano con un ojo. El estado lo marcan la forma y el movimiento,
  no el color, así que el color es libre (prop `color`).

  USO
    <AndyMascot state="thinking" :size="28" />
    <AndyMascot state="done" :size="36" @done="state = 'idle'" />
    <AndyMascot state="alert" :size="48" :badge-count="3" />

  ESTADOS (prop `state`)
    idle         Respira, parpadea doble y mira a los lados. Cada `idleSpinEvery` segundos se agacha y da una vuelta.
    thinking     Tres puntos que saltan por turnos y vaivén suave.
    consulting   Bucle: tres puntos -> el ojo se abre y mira alrededor -> el ojo se cierra en los puntos.
    searching    Ojo bien abierto; la pupila salta de punto en punto, como recorriendo las baldas de una librería.
    reading      Inclinada, ojo achinado; la pupila lee la línea, salta a la siguiente y parpadea.
    done         Dos cabeceos con aplastamiento y rebote (una sola vez). Emite `done` al terminar.
    alert        Saltitos repetidos con insignia de notificación que late.

  PROPS
    state          String   'idle' | 'thinking' | 'consulting' | 'searching' | 'reading' | 'done' | 'alert'
    size           Number   Lado en px. Por defecto 36. Para chat: 28.
    color          String   Color del cuerpo (cualquier color CSS). Por defecto #157D82.
    animated       Boolean  Activa el movimiento. Por defecto true. Se desactiva solo si el usuario pide menos movimiento.
    idleSpinEvery  Number   Segundos entre vueltas en `idle`. 0 la desactiva. Por defecto 20.
    badgeCount     Number|String  Número de la insignia de `alert`. Solo se escribe a partir de 48 px. Por defecto 1.
    pupilColor     String   Color de la pupila. Por defecto #26272b.
    decorative     Boolean  Oculta la mascota a lectores de pantalla (úsalo si el estado ya está escrito al lado).
    label          String   Texto accesible. Por defecto «Andy, <estado>».

  EVENTOS
    done           Termina la animación de `done`.

  MÉTODOS
    replay()       Reinicia las animaciones de un solo disparo (`done`) y vuelve a lanzar la vuelta de `idle`.

  Notas
    - Sin dependencias. No usa SMIL: solo SVG y animaciones CSS.
    - Los textos de estado y el área táctil, si el avatar es pulsable, los pone quien lo use (mín. 44 px).
-->
<template>
  <svg
    class="andy-mascot"
    :class="{ 'is-animated': isAnimated }"
    :width="size"
    :height="size"
    viewBox="0 0 100 100"
    focusable="false"
    :role="decorative ? null : 'img'"
    :aria-label="decorative ? null : ariaLabel"
    :aria-hidden="decorative ? 'true' : null"
  >
    <g :key="replayKey" :class="wrapClass" @animationend="onWrapEnd">
      <g :class="swayClass">
        <g :class="bodyClass" @animationend="onBodyEnd">
          <circle cx="50" cy="50" r="50" :fill="color" />

          <!-- idle -->
          <g v-if="state === 'idle'" class="am-blink">
            <ellipse cx="50" cy="48" rx="26" ry="26" fill="#fff" />
            <g class="am-glance"><g class="am-pupil-track"><ellipse cx="50" cy="48" rx="9.5" ry="9.5" :fill="pupilColor" /></g></g>
          </g>

          <!-- thinking -->
          <g v-else-if="state === 'thinking'">
            <circle class="am-dot" cx="31" cy="52" r="7" fill="#fff" />
            <circle class="am-dot" cx="50" cy="52" r="7" fill="#fff" />
            <circle class="am-dot" cx="69" cy="52" r="7" fill="#fff" />
          </g>

          <!-- consulting: puntos <-> ojo, en bucle -->
          <g v-else-if="state === 'consulting'">
            <g class="am-cdots">
              <g class="am-cd am-cd1"><circle class="am-dot" cx="31" cy="52" r="7" fill="#fff" /></g>
              <g class="am-cd am-cd2"><circle class="am-dot" cx="50" cy="52" r="7" fill="#fff" /></g>
              <g class="am-cd am-cd3"><circle class="am-dot" cx="69" cy="52" r="7" fill="#fff" /></g>
            </g>
            <g v-if="isAnimated" class="am-ceye">
              <g class="am-blink">
                <ellipse cx="50" cy="48" rx="26" ry="26" fill="#fff" />
                <g class="am-dart"><ellipse cx="50" cy="48" rx="8.8" ry="8.8" :fill="pupilColor" /></g>
              </g>
            </g>
          </g>

          <!-- searching -->
          <g v-else-if="state === 'searching'" class="am-blink">
            <ellipse cx="50" cy="48" rx="26" ry="26" fill="#fff" />
            <g class="am-dart"><ellipse cx="50" cy="48" rx="8.8" ry="8.8" :fill="pupilColor" /></g>
          </g>

          <!-- reading -->
          <g v-else-if="state === 'reading'" class="am-squint">
            <ellipse cx="50" cy="48" rx="26" ry="26" fill="#fff" />
            <g class="am-scan"><ellipse cx="50" cy="48" rx="10.7" ry="10.7" :fill="pupilColor" /></g>
          </g>

          <!-- done -->
          <g v-else-if="state === 'done'" class="am-blink">
            <ellipse cx="50" cy="48" rx="26" ry="26" fill="#fff" />
            <g class="am-nodpupil"><ellipse cx="50" cy="48" rx="9.5" ry="9.5" :fill="pupilColor" /></g>
          </g>

          <!-- alert -->
          <g v-else-if="state === 'alert'" class="am-blink">
            <ellipse cx="50" cy="48" rx="26" ry="26" fill="#fff" />
            <g style="transform: translate(3px, -3px)"><ellipse cx="50" cy="48" rx="10.5" ry="10.5" :fill="pupilColor" /></g>
          </g>
        </g>
      </g>
    </g>

    <!-- insignia de notificación -->
    <g v-if="state === 'alert'" class="am-badge">
      <circle cx="86" cy="16" r="15" fill="#e4383b" stroke="#fff" stroke-width="3.5" />
      <text v-if="showBadgeNumber" x="86" y="22.5" text-anchor="middle" font-size="19" font-weight="700" fill="#fff" font-family="system-ui, sans-serif">{{ badgeCount }}</text>
    </g>
  </svg>
</template>

<script>
const STATES = {
  idle: 'idle',
  thinking: 'thinking',
  consulting: 'consulting',
  searching: 'searching',
  reading: 'reading',
  done: 'done',
  alert: 'alert'
}

export default {
  name: 'AndyMascot',
  props: {
    state: {
      type: String,
      default: 'idle',
      validator: value => Object.prototype.hasOwnProperty.call(STATES, value)
    },
    size: { type: Number, default: 36 },
    color: { type: String, default: '#157D82' },
    animated: { type: Boolean, default: true },
    idleSpinEvery: { type: Number, default: 20 },
    badgeCount: { type: [Number, String], default: 1 },
    pupilColor: { type: String, default: '#26272b' },
    decorative: { type: Boolean, default: false },
    label: { type: String, default: '' }
  },
  data () {
    return {
      reduceMotion: false,
      spinning: false,
      replayKey: 0,
      spinTimer: null,
      mediaQuery: null
    }
  },
  computed: {
    isAnimated () {
      return this.animated && !this.reduceMotion
    },
    ariaLabel () {
      return this.label || 'Andy, ' + STATES[this.state]
    },
    wrapClass () {
      return this.state === 'idle' ? ['am-idlewrap', { 'am-spin-once': this.spinning }] : ''
    },
    swayClass () {
      return this.state === 'idle' ? 'am-sway' : ''
    },
    bodyClass () {
      switch (this.state) {
        case 'idle': return 'am-breathe'
        case 'thinking': return 'am-think'
        case 'consulting': return this.isAnimated ? 'am-consult' : ''
        case 'searching': return 'am-seek'
        case 'reading': return 'am-lean'
        case 'done': return 'am-nod am-grounded'
        case 'alert': return 'am-hops am-grounded'
        default: return ''
      }
    },
    showBadgeNumber () {
      return this.size >= 48 && String(this.badgeCount) !== ''
    }
  },
  watch: {
    state () { this.spinning = false; this.armSpin() },
    isAnimated () { this.spinning = false; this.armSpin() },
    idleSpinEvery () { this.armSpin() }
  },
  mounted () {
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
      this.reduceMotion = this.mediaQuery.matches
      if (this.mediaQuery.addEventListener) this.mediaQuery.addEventListener('change', this.onMotionChange)
      else if (this.mediaQuery.addListener) this.mediaQuery.addListener(this.onMotionChange)
    }
    this.armSpin()
  },
  beforeDestroy () {
    this.clearSpin()
    if (this.mediaQuery) {
      if (this.mediaQuery.removeEventListener) this.mediaQuery.removeEventListener('change', this.onMotionChange)
      else if (this.mediaQuery.removeListener) this.mediaQuery.removeListener(this.onMotionChange)
    }
  },
  methods: {
    onMotionChange (e) {
      this.reduceMotion = e.matches
    },
    clearSpin () {
      if (this.spinTimer) {
        clearInterval(this.spinTimer)
        this.spinTimer = null
      }
    },
    armSpin () {
      this.clearSpin()
      if (this.state === 'idle' && this.isAnimated && this.idleSpinEvery > 0) {
        this.spinTimer = setInterval(this.spinOnce, this.idleSpinEvery * 1000)
      }
    },
    spinOnce () {
      if (this.state !== 'idle' || !this.isAnimated) return
      this.spinning = false
      this.$nextTick(() => { this.spinning = true })
    },
    onWrapEnd (e) {
      if (e.target === e.currentTarget) this.spinning = false
    },
    onBodyEnd (e) {
      if (e.target === e.currentTarget && this.state === 'done') this.$emit('done')
    },
    replay () {
      this.replayKey += 1
      this.spinning = false
      if (this.state === 'idle') this.$nextTick(this.spinOnce)
    }
  }
}
</script>

<style scoped>
.andy-mascot { display: block; overflow: visible; }
.andy-mascot * { transform-box: fill-box; transform-origin: center; }
.andy-mascot .am-grounded { transform-origin: 50% 100%; }

.is-animated .am-breathe { animation: am-breathe 3.6s ease-in-out infinite; }
.is-animated .am-sway { animation: am-sway 9s ease-in-out infinite; }
.is-animated .am-pupil-track { animation: am-dilate 9s ease-in-out infinite; }
.is-animated .am-squint { animation: am-squint 4.8s ease-in-out infinite; }
.is-animated .am-think { animation: am-think 2.4s ease-in-out infinite alternate; }
.is-animated .am-blink { animation: am-blink 5.5s infinite; }
.is-animated .am-glance { animation: am-glance 9s ease-in-out infinite; }
.is-animated .am-scan { animation: am-scan 3.6s ease-in-out infinite; }
.is-animated .am-seek { animation: am-seek 1.6s ease-in-out infinite alternate; }
.is-animated .am-dart { animation: am-dart 3.4s ease-in-out infinite; }
.is-animated .am-lean { animation: am-lean 2.8s ease-in-out infinite alternate; }
.is-animated .am-dot { animation: am-dot 1.2s ease-in-out infinite; }
.is-animated .am-dot:nth-child(2) { animation-delay: .2s; }
.is-animated .am-dot:nth-child(3) { animation-delay: .4s; }
.is-animated .am-nod { animation: am-nod 1.7s cubic-bezier(.3, .7, .4, 1) 1; }
.is-animated .am-nodpupil { animation: am-nodpupil 1.7s ease-in-out 1; }
.is-animated .am-hops { animation: am-hops 1.9s cubic-bezier(.3, .7, .4, 1) infinite; }
.is-animated .am-badge { animation: am-badge 1.9s ease-in-out infinite; }
.is-animated .am-spin-once { animation: am-spin 1.5s cubic-bezier(.4, 0, .2, 1) 1; }
.is-animated .am-consult { animation: am-consult 4.8s ease-in-out infinite; }
.is-animated .am-cd1 { animation: am-cd1 4.8s ease-in-out infinite; }
.is-animated .am-cd2 { animation: am-cd2 4.8s ease-in-out infinite; }
.is-animated .am-cd3 { animation: am-cd3 4.8s ease-in-out infinite; }
.is-animated .am-ceye { animation: am-ceye 4.8s ease-in-out infinite; transform: scale(0); }

@keyframes am-spin { 0% { transform: rotate(0) scale(1, 1); } 14% { transform: rotate(0) scale(1.1, .86); } 55% { transform: rotate(385deg) scale(.95, 1.08); } 76% { transform: rotate(354deg) scale(1.04, .97); } 100% { transform: rotate(360deg) scale(1, 1); } }
@keyframes am-sway { 0%, 18% { transform: rotate(0); } 24%, 38% { transform: rotate(-5deg); } 46%, 60% { transform: rotate(0); } 68%, 82% { transform: rotate(5deg); } 90%, 100% { transform: rotate(0); } }
@keyframes am-dilate { 0%, 18% { transform: scale(1); } 24%, 38% { transform: scale(1.22); } 46%, 60% { transform: scale(1); } 68%, 82% { transform: scale(1.22); } 90%, 100% { transform: scale(1); } }
@keyframes am-squint { 0%, 100% { transform: scaleY(.86); } 40% { transform: scaleY(.74); } 44% { transform: scaleY(.3); } 48% { transform: scaleY(.8); } 88% { transform: scaleY(.74); } 92% { transform: scaleY(.3); } 96% { transform: scaleY(.86); } }
@keyframes am-think { 0% { transform: rotate(-6deg) translateY(1%) scale(1.02, .98); } 100% { transform: rotate(6deg) translateY(-3%) scale(.98, 1.03); } }
@keyframes am-seek { from { transform: rotate(-4deg) translateY(1%); } to { transform: rotate(4deg) translateY(-2%); } }
@keyframes am-dart { 0%, 8% { transform: translate(-10px, -8px); } 12%, 24% { transform: translate(10px, -8px); } 28%, 40% { transform: translate(10px, 7px); } 44%, 56% { transform: translate(-10px, 7px); } 60%, 70% { transform: translate(1px, -10px); } 74%, 88% { transform: translate(-5px, 2px); } 100% { transform: translate(-10px, -8px); } }
@keyframes am-consult { 0% { transform: rotate(-8deg) translateY(1%) scale(1, 1); } 17% { transform: rotate(8deg) translateY(-3%) scale(.98, 1.03); } 34% { transform: rotate(-6deg) translateY(1%) scale(1.02, .98); } 44% { transform: rotate(0) translateY(2%) scale(1.07, .92); } 54% { transform: rotate(-4deg) translateY(-3%) scale(.95, 1.06); } 70% { transform: rotate(4deg) translateY(-1%) scale(1, 1); } 84% { transform: rotate(-3deg) translateY(1%) scale(1, 1); } 91% { transform: rotate(0) translateY(2%) scale(1.07, .92); } 100% { transform: rotate(-8deg) translateY(1%) scale(1, 1); } }
@keyframes am-cd1 { 0%, 30% { transform: translate(0, 0) scale(1); opacity: 1; } 42%, 90% { transform: translate(19px, -4px) scale(.3); opacity: 0; } 100% { transform: translate(0, 0) scale(1); opacity: 1; } }
@keyframes am-cd2 { 0%, 30% { transform: translate(0, 0) scale(1); opacity: 1; } 42%, 90% { transform: translate(0, -4px) scale(.3); opacity: 0; } 100% { transform: translate(0, 0) scale(1); opacity: 1; } }
@keyframes am-cd3 { 0%, 30% { transform: translate(0, 0) scale(1); opacity: 1; } 42%, 90% { transform: translate(-19px, -4px) scale(.3); opacity: 0; } 100% { transform: translate(0, 0) scale(1); opacity: 1; } }
@keyframes am-ceye { 0%, 40% { transform: scale(0); } 53% { transform: scale(1.1); } 58%, 83% { transform: scale(1); } 92%, 100% { transform: scale(0); } }
@keyframes am-breathe { 0%, 100% { transform: scale(1, 1); } 50% { transform: scale(1.05, .97); } }
@keyframes am-blink { 0%, 88%, 100% { transform: scaleY(1); } 90% { transform: scaleY(.08); } 92% { transform: scaleY(1); } 94% { transform: scaleY(.08); } 96% { transform: scaleY(1); } }
@keyframes am-glance { 0%, 18% { transform: translate(0, 0); } 24%, 38% { transform: translate(-9px, -2px); } 46%, 60% { transform: translate(0, 0); } 68%, 82% { transform: translate(9px, 2px); } 90%, 100% { transform: translate(0, 0); } }
@keyframes am-scan { 0% { transform: translate(-10px, -3px); } 38% { transform: translate(10px, -3px); } 46% { transform: translate(-10px, 4px); } 84% { transform: translate(10px, 4px); } 92%, 100% { transform: translate(-10px, -3px); } }
@keyframes am-lean { from { transform: rotate(-7deg) translateY(1%); } to { transform: rotate(3deg) translateY(-2%); } }
@keyframes am-dot { 0%, 70%, 100% { transform: translateY(0) scale(.6); opacity: .5; } 35% { transform: translateY(-30%) scale(1.15); opacity: 1; } }
@keyframes am-nod { 0% { transform: translateY(0) scale(1, 1); } 10% { transform: translateY(-7%) scale(.97, 1.05); } 24% { transform: translateY(14%) scale(1.07, .9); } 40% { transform: translateY(-5%) scale(.98, 1.04); } 54% { transform: translateY(12%) scale(1.06, .92); } 72% { transform: translateY(-3%) scale(.99, 1.02); } 100% { transform: translateY(0) scale(1, 1); } }
@keyframes am-nodpupil { 0%, 100% { transform: translateY(0) scale(1); } 24%, 54% { transform: translateY(6px) scale(1); } 40% { transform: translateY(0) scale(1); } 76%, 90% { transform: translateY(0) scale(1.22); } }
@keyframes am-hops { 0%, 60%, 100% { transform: translateY(0) scale(1, 1); } 6% { transform: translateY(0) scale(1.08, .9); } 20% { transform: translateY(-18%) scale(.94, 1.08); } 34% { transform: translateY(0) scale(1.07, .92); } 44% { transform: translateY(-8%) scale(.98, 1.03); } 54% { transform: translateY(0) scale(1, 1); } }
@keyframes am-badge { 0%, 60%, 100% { transform: scale(1); } 66% { transform: scale(1.25); } 74% { transform: scale(.95); } 82% { transform: scale(1); } }

@media (prefers-reduced-motion: reduce) {
  .andy-mascot * { animation: none !important; }
}
</style>
